import { useEffect, useState, useCallback } from "react";
import { api, apiError } from "@/lib/api";
import { Plus, Users, Pencil } from "lucide-react";
import { Loading, EmptyState } from "@/components/common";
import { WhatsAppButton } from "@/components/common";
import { formatDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

const empty = { business_name: "", contact_person: "", mobile: "", whatsapp: "", email: "", portal_password: "" };

export default function Customers() {
  const [customers, setCustomers] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [f, setF] = useState(empty);

  const load = useCallback(async () => setCustomers((await api.get("/customers")).data), []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setF(empty); setOpen(true); };
  const openEdit = (c) => { setEditing(c); setF({ ...empty, ...c, portal_password: "" }); setOpen(true); };
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const save = async () => {
    if (!f.business_name) { toast.error("Business name required"); return; }
    try {
      if (editing) { await api.put(`/customers/${editing.id}`, f); toast.success("Customer updated"); }
      else { await api.post("/customers", f); toast.success("Customer added"); }
      setOpen(false); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  if (customers === null) return <Loading />;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold font-display text-slate-900">Customers</h1>
          <p className="text-sm text-slate-500 mt-1">Manage customers and their portal access.</p>
        </div>
        <Button data-testid="add-customer-button" onClick={openNew} className="bg-[#1e3a8a] hover:bg-[#1e40af]"><Plus className="h-4 w-4 mr-1.5" /> Add Customer</Button>
      </div>

      {customers.length === 0 ? (
        <div className="bg-white border rounded-xl"><EmptyState icon={Users} title="No customers yet" sub="Add a customer to start creating orders." /></div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {customers.map((c) => (
            <div key={c.id} data-testid={`customer-card-${c.id}`} className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-semibold text-slate-900 font-display">{c.business_name}</div>
                  <div className="text-sm text-slate-500">{c.contact_person}</div>
                </div>
                <button onClick={() => openEdit(c)} className="h-8 w-8 grid place-items-center rounded-lg hover:bg-slate-100 text-slate-500" data-testid={`edit-customer-${c.id}`}><Pencil className="h-4 w-4" /></button>
              </div>
              <div className="mt-3 space-y-1 text-sm text-slate-600">
                {c.mobile && <div>📱 {c.mobile}</div>}
                {c.email && <div className="truncate">✉ {c.email}</div>}
              </div>
              <div className="mt-3 flex items-center justify-between">
                <span className={`text-xs px-2 py-0.5 rounded-full ${c.user_id ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{c.user_id ? "Portal enabled" : "No portal login"}</span>
                {(c.whatsapp || c.mobile) && <WhatsAppButton number={c.whatsapp || c.mobile} label="WhatsApp" />}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-display">{editing ? "Edit Customer" : "Add Customer"}</DialogTitle></DialogHeader>
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2"><Label>Business Name *</Label><Input data-testid="cf-business" value={f.business_name} onChange={(e) => set("business_name", e.target.value)} className="mt-1.5" /></div>
            <div><Label>Contact Person</Label><Input data-testid="cf-contact" value={f.contact_person} onChange={(e) => set("contact_person", e.target.value)} className="mt-1.5" /></div>
            <div><Label>Mobile</Label><Input data-testid="cf-mobile" value={f.mobile} onChange={(e) => set("mobile", e.target.value)} className="mt-1.5" /></div>
            <div><Label>WhatsApp</Label><Input data-testid="cf-whatsapp" value={f.whatsapp} onChange={(e) => set("whatsapp", e.target.value)} className="mt-1.5" /></div>
            <div><Label>Email</Label><Input data-testid="cf-email" type="email" value={f.email} onChange={(e) => set("email", e.target.value)} className="mt-1.5" /></div>
            <div className="sm:col-span-2">
              <Label>Portal Password {editing ? "(leave blank to keep)" : "(optional — enables customer login)"}</Label>
              <Input data-testid="cf-password" type="text" value={f.portal_password} onChange={(e) => set("portal_password", e.target.value)} className="mt-1.5" placeholder="Set a password to enable portal login" />
              <p className="text-xs text-slate-500 mt-1">Customer logs in with their email + this password.</p>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button data-testid="save-customer" onClick={save} className="bg-[#1e3a8a]">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
